import { useEffect, useState } from 'react';
import { DEFAULT_THEME, type ThemeName, themes } from './tokens';

const STORAGE_KEY = 'sourcito-theme';

function isThemeName(value: unknown): value is ThemeName {
  return typeof value === 'string' && value in themes;
}

function readStoredTheme(): ThemeName {
  if (typeof window === 'undefined') return DEFAULT_THEME;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return isThemeName(stored) ? stored : DEFAULT_THEME;
}

function applyTheme(name: ThemeName) {
  const root = document.documentElement;
  for (const themeName of Object.keys(themes)) {
    root.classList.remove(`theme-${themeName}`);
  }
  root.classList.add(`theme-${name}`);
}

/**
 * useTheme — read the current theme and switch it.
 * Persists to localStorage. Apply happens via effect on mount and on change.
 */
export function useTheme() {
  const [theme, setThemeState] = useState<ThemeName>(() => readStoredTheme());

  useEffect(() => {
    applyTheme(theme);
    window.localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  return { theme, setTheme: setThemeState };
}

/**
 * Apply the persisted theme synchronously, before React hydrates.
 * Call once at module load time in main.tsx to avoid a flash on warm-light users.
 */
export function bootstrapTheme() {
  if (typeof window === 'undefined') return;
  applyTheme(readStoredTheme());
}
